import './games.css'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Flame, Check, ArrowRight, Share2 } from 'lucide-react'
import { GAMES, gameByKey, slateResults, fullSlateRun, ordinal } from './gameMeta'
import { CountTicker } from '../CountTicker'
import { useNextGame } from './GameFrame'
import { logEvent } from '../../lib/analytics'
import { useAuth } from '../../hooks/useAuth'
import { useFollows } from '../../hooks/useFollows'
import { friendsResults, profileName } from '../../lib/gameSocial'

// ── GameCurtain — the outro every daily lands on ─────────────────────────────
// Fires ONCE when a day is actually finished (never on the same-day restore):
// the score punches onto a dark stage, the day's tiles land, the streak reads,
// and the way out is the next unplayed daily. The third game of the day adds the
// Full Slate beat. Tap anywhere to drop the curtain; it also drops itself.
// Skipped in tests (the result underneath carries the same numbers) and reduced
// to a still card under prefers-reduced-motion.
export function GameCurtain({ game, day, score, denom, headline, tiles, streak = 0, fullSlate = false, onDone, holdMs = 6500 }) {
  const navigate = useNavigate()
  const g = gameByKey(game)
  const next = useNextGame(game, day)
  // A friend who already played today becomes the bar: "Melissa 4/5 · you 5/5".
  const { user } = useAuth()
  const { followingIds } = useFollows(user?.id)
  const [rival, setRival] = useState(null)
  useEffect(() => {
    let alive = true
    if (!user?.id || !followingIds?.length || day == null) return
    friendsResults(followingIds.slice(0, 200), [day]).then(rows => { if (alive) setRival(rows.find(r => r.key === game && r.profile) || null) }).catch(() => {})
    return () => { alive = false }
  }, [user?.id, day, game, (followingIds || []).join(',')])   // eslint-disable-line react-hooks/exhaustive-deps
  const Icon = g.Icon
  const [out, setOut] = useState(false)
  const doneRef = useRef(false)
  const close = (how) => {
    if (doneRef.current) return
    doneRef.current = true
    setOut(true)
    setTimeout(() => { window.__nextepModalHold = false; onDone?.(how) }, 260)
  }
  useEffect(() => {
    window.__nextepModalHold = true
    logEvent('game_curtain', { game, next: next?.key || null, full_slate: fullSlate })
    const t = setTimeout(() => close('auto'), fullSlate ? holdMs + 6000 : holdMs)   // the slate sheet is worth the extra read
    return () => { clearTimeout(t); window.__nextepModalHold = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const goNext = (e) => { e.stopPropagation(); close('next'); setTimeout(() => navigate(next.route), 200) }

  return createPortal(
    <div className={`gm-stage${out ? ' gm-out' : ''}`} style={{ '--gm-tone': g.tone, cursor: 'pointer' }} onClick={() => close('tap')} role="dialog" aria-label={`${g.label} result`}>
      <div className="gm-glow a" /><div className="gm-glow b" />
      <div className="gm-glyph-in" style={{ width: 56, height: 56, borderRadius: 16, background: g.tone, color: 'var(--amber-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 14px 36px -12px color-mix(in srgb, ${g.tone} 70%, transparent)` }}>
        <Icon size={26} strokeWidth={2.2} />
      </div>
      <div className="gm-eyebrow gm-rise" style={{ '--d': '140ms', color: g.tone, marginTop: 18 }}>{g.label} · today</div>
      <div className="gm-score gm-punch" style={{ '--d': '300ms', fontSize: fullSlate ? 64 : 92, color: 'var(--text-strong)', marginTop: 6 }}>
        {score}{denom && <span style={{ fontSize: 26, color: 'var(--text-dim)', marginLeft: 4, letterSpacing: 0 }}>{denom}</span>}
      </div>
      {headline && <div className="gm-rise" style={{ '--d': '640ms', fontFamily: 'var(--font-serif)', fontSize: 23, color: 'var(--text-strong)', marginTop: 10, textAlign: 'center', maxWidth: 300, lineHeight: 1.2, textWrap: 'balance' }}>{headline}</div>}
      {tiles && !fullSlate && <div style={{ display: 'flex', gap: 8, marginTop: 18 }} aria-hidden="true">{tiles}</div>}
      {streak > 0 && (
        <div className="gm-rise" style={{ '--d': '1200ms', display: 'flex', alignItems: 'center', gap: 6, color: 'var(--amber)', fontSize: 14, fontWeight: 700, marginTop: 16 }}>
          <Flame size={15} fill="currentColor" />{streak}-day streak
        </div>
      )}
      {rival && !fullSlate && (
        <div className="gm-rise" style={{ '--d': '1350ms', display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, color: 'var(--text-muted)', fontSize: 13.5 }}>
          <span style={{ color: 'var(--social)', fontWeight: 700 }}>{profileName(rival.profile)}</span> {rival.short} · <span style={{ color: 'var(--text-strong)', fontWeight: 700 }}>you {score}{denom || ''}</span>
          <span className="gm-eyebrow" style={{ color: 'var(--amber)' }}>{Number(score) > rival.score ? 'ahead' : Number(score) === rival.score ? 'level' : 'behind'}</span>
        </div>
      )}
      {fullSlate && <FullSlate day={day} current={game} score={score} denom={denom} />}
      <div className="gm-rise" style={{ '--d': fullSlate ? '2600ms' : '1600ms', display: 'flex', gap: 10, marginTop: 30 }}>
        <button type="button" className="gm-pill" onClick={(e) => { e.stopPropagation(); close('continue') }}><Check size={16} /> See the result</button>
        {next && <button type="button" className="gm-pill primary" onClick={goNext} style={{ whiteSpace: 'nowrap' }} aria-label={`Next up: ${next.label}`}><next.Icon size={17} strokeWidth={2.4} /> Next up: {next.label} <ArrowRight size={16} /></button>}
      </div>
      {!next && <div className="gm-rise" style={{ '--d': '1800ms', color: 'var(--text-dim)', fontSize: 12.5, marginTop: 14 }}>That’s the slate. Same time tomorrow.</div>}
    </div>,
    document.body,
  )
}

// Convenience: whether a curtain should play at all in this environment.
export const curtainAllowed = () => typeof window !== 'undefined' && import.meta.env.MODE !== 'test'

// ── FullSlate — the third daily of the day earns the whole sheet ──────────────
// An act, not a ribbon: the three tones take over the glow, two gold rules draw in
// around the eyebrow, the headline rises, then the three slates fan in one after
// another, each score counting up as its card lands, and a single sheen sweeps across
// all three. The line under the headline is what makes it mean something: the first
// full slate reads as a first, after that it's "N days running". Scores come from
// markPlayed's recorded results (one source); the game finishing right now is passed
// in directly because its record and this render share a tick.
function FullSlate({ day, current, score, denom }) {
  const rows = slateResults(day).map(g => g.key === current ? { ...g, result: { score, denom } } : g)
  const run = fullSlateRun(day)
  const meaning = run <= 1 ? 'Your first full slate.' : `${run} days running.`
  // The counts start when the cards have landed, not at mount.
  const [go, setGo] = useState(false)
  useEffect(() => { const t = setTimeout(() => setGo(true), 1750); return () => clearTimeout(t) }, [])
  const line = rows.map(g => `${g.label} ${g.result ? `${g.result.score}${g.result.denom === 'pts' ? ' pts' : (g.result.denom || '')}` : '✓'}`).join(' · ')
  const text = `Full slate on NextEp · Day ${day}${run > 1 ? ` · ${run} days running` : ''}\n${line}\nnextep.show/live`
  const share = async (e) => {
    e.stopPropagation()
    logEvent('full_slate_share', { day, run })
    try { if (navigator.share) await navigator.share({ title: 'Full slate on NextEp', text }); else await navigator.clipboard.writeText(text) } catch {}
  }
  return (
    <div className="gm-slate" onClick={(e) => e.stopPropagation()}>
      <div className="gm-glow c" aria-hidden="true" />
      <div className="gm-slate-head gm-rise" style={{ '--d': '1250ms' }}>
        <span className="gm-rule l" aria-hidden="true" /><span className="gm-eyebrow" style={{ color: 'var(--amber)' }}>Full slate · day {day}</span><span className="gm-rule r" aria-hidden="true" />
      </div>
      <div className="gm-rise" style={{ '--d': '1400ms', fontFamily: 'var(--font-serif)', fontSize: 30, color: 'var(--text-strong)', textAlign: 'center', marginTop: 6, letterSpacing: '-0.01em', lineHeight: 1.1 }}>All three, today.</div>
      <div className="gm-rise" style={{ '--d': '1550ms', color: run > 1 ? 'var(--amber)' : 'var(--text-muted)', fontSize: 14, fontWeight: 600, textAlign: 'center', marginTop: 6 }}>{meaning}</div>
      <div className="gm-slate-row">
        {rows.map((g, i) => { const I = g.Icon; const r = g.result; return (
          <div key={g.key} className="gm-slate-card" style={{ '--d': `${1700 + i * 220}ms`, '--gm-tone': g.tone, '--rot': `${(i - 1) * 2.5}deg` }}>
            <span className="gm-sheen" aria-hidden="true" style={{ '--d': `${2500 + i * 90}ms` }} />
            <span className="gm-glyph-in" style={{ '--d': `${1850 + i * 220}ms`, width: 34, height: 34, borderRadius: 10, background: g.tone, color: 'var(--amber-ink)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><I size={18} strokeWidth={2.4} /></span>
            <div className="gm-score" style={{ fontSize: 32, color: 'var(--text-strong)', marginTop: 8, lineHeight: 1 }}>
              {r ? (go ? <CountTicker value={Number(r.score)} from={0} duration={700 + i * 120} /> : 0) : '✓'}
              {r?.denom && <span style={{ fontSize: 12, color: 'var(--text-dim)', marginLeft: 2, letterSpacing: 0 }}>{r.denom === 'pts' ? ' pts' : r.denom}</span>}
            </div>
            <div className="gm-eyebrow" style={{ color: g.tone, marginTop: 7, fontSize: 9.5 }}>{g.label}</div>
          </div>
        ) })}
      </div>
      <div className="gm-rise" style={{ '--d': '2700ms', display: 'flex', justifyContent: 'center', marginTop: 16 }}>
        <button type="button" className="gm-pill" onClick={share}><Share2 size={15} /> Share the slate</button>
      </div>
    </div>
  )
}
